'use client';

import {
  FilterRailFacet,
  FilterRailOptions,
  FilterRailScroller,
  FilterRailSearch,
  FilterRailShowAll,
  useFilterRailOptions,
} from '@vs-shop/commerce-ui/filterRail';
import { EmptyState, EmptyStateVariant } from '@vs-shop/commerce-ui/emptyState';
import type { FuseOptionKey } from 'fuse.js';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';

import { minItemsForSearch } from '../../../shared/config/productFilters';

type Props<T extends { label: string }> = {
  items: T[];
  getKeyAction: (item: T) => string;
  renderItemAction: (item: T) => ReactNode;
  searchPlaceholder: string;
  // Fuse fuzziness; lower is stricter. Defaults to the shared rail's own value.
  searchThreshold?: number;
  // Fuse search keys. Defaults to matching `label` only; dimensions whose options
  // carry a secondary searchable field pass it too (choice → `value`, brand →
  // `code`), preserving the per-dimension match surface of the pre-convergence lists.
  searchKeys?: FuseOptionKey<T>[];
  // When set, only this many items render until "show all" is pressed. Omit to
  // render every (filtered) item with no "show all" affordance (brand / taxon).
  initialVisibleCount?: number | undefined;
};

/**
 * rcprofi's arrangement of a searchable, scrollable filter facet: the fuzzy
 * search box (shown past `minItemsForSearch`), the scroll container, the
 * optional "show all" slice, and the empty state. Dimension-agnostic — the
 * caller supplies the option view-models and an item renderer that owns the
 * atom subscription. Used by the multiple-choice, brand, and taxon dimensions
 * across all rails.
 *
 * The parts and the option state are `@vs-shop/commerce-ui`'s. What stays here
 * is rcprofi's: the copy, and the two thresholds it configures.
 */
export default function FilterChoiceList<T extends { label: string }>({
  items,
  getKeyAction,
  renderItemAction,
  searchPlaceholder,
  searchThreshold,
  searchKeys,
  initialVisibleCount,
}: Props<T>) {
  const intl = useIntl();
  const { query, setQuery, visibleItems, isSearchable, hasHiddenItems, showAll } =
    useFilterRailOptions({
      items,
      searchKeys,
      searchThreshold,
      initialVisibleCount,
      minItemsForSearch,
    });

  return (
    <FilterRailFacet>
      {isSearchable && (
        <FilterRailSearch
          id="filter-search"
          value={query}
          onValueChange={setQuery}
          placeholder={searchPlaceholder}
          label={intl.formatMessage({
            id: 'component.client.filter.filterSearch.label',
            defaultMessage: 'Hledat',
          })}
          clearLabel={intl.formatMessage({
            id: 'component.client.filter.filterSearch.clear',
            defaultMessage: 'Vymazat hledání',
          })}
        />
      )}

      <FilterRailScroller>
        {visibleItems.length > 0 ? (
          <>
            <FilterRailOptions>
              {visibleItems.map((item) => (
                <Fragment key={getKeyAction(item)}>{renderItemAction(item)}</Fragment>
              ))}
            </FilterRailOptions>

            {hasHiddenItems ? (
              <FilterRailShowAll onClick={showAll}>
                {intl.formatMessage({
                  id: 'filter.items_shown',
                  defaultMessage: 'Zobrazit všechny',
                })}
              </FilterRailShowAll>
            ) : null}
          </>
        ) : (
          <EmptyState variant={EmptyStateVariant.INLINE} className="py-8 text-sm">
            {intl.formatMessage({
              id: 'component.client.filter.filterSearch.noResults',
              defaultMessage: 'Nenalezeny žádné výsledky',
            })}
          </EmptyState>
        )}
      </FilterRailScroller>
    </FilterRailFacet>
  );
}
